import { useTranslation } from 'react-i18next'

import {
  FieldBooleanBadge,
  FieldExternalLink,
  FieldGroup,
  FieldGroupHeader,
  FieldPhotoImage,
  FieldRow,
  FieldsTable,
  FieldViewValue,
} from '@/components/layout/field-row'
import { Badge } from '@/components/ui/badge'
import { getAssetLink } from '@/helpers/get-asset-link'
import { Building } from '@/reducers/types'

import { PlaceFeatureRow } from '../../flags/place-feature-row'
import {
  getPlaceGeneralDefaults,
  getPlaceGeneralLayout,
  getPlaceGeneralRowLabelKey,
  PLACE_GENERAL_FLAG_ROWS,
  PLACE_GENERAL_LINK_ROWS,
  PLACE_GENERAL_PHONE_ROWS,
  PLACE_GENERAL_PHOTO_ROWS,
  PLACE_GENERAL_SWITCH_ROWS,
  PlaceGeneralFlagKey,
  PlaceGeneralRowId,
  PlaceGeneralSwitchKey,
} from './general-tab.constants'

interface GeneralViewFieldsProps {
  building: Building
  countryLabel: string
  isAdmin: boolean
  timezoneLabel: string
}

export const GeneralViewFields = ({
  building,
  countryLabel,
  isAdmin,
  timezoneLabel,
}: GeneralViewFieldsProps) => {
  const { t } = useTranslation(['organisation'])
  const values = getPlaceGeneralDefaults(building)

  const renderValue = (row: PlaceGeneralRowId) => {
    if (row === 'country') return countryLabel
    if (row === 'timezone') return timezoneLabel
    if (row === 'tags') {
      return values.tags.length === 0
        ? null
        : values.tags.map((tag) => (
            <Badge key={tag} color="blue" size="small" variant="light">
              {tag}
            </Badge>
          ))
    }
    if (PLACE_GENERAL_SWITCH_ROWS.includes(row)) {
      return <FieldBooleanBadge value={values[row as PlaceGeneralSwitchKey]} />
    }
    if (row === 'residentConvenienceFeeRate') {
      return values.residentConvenienceFeeRate && `${values.residentConvenienceFeeRate}%`
    }
    if (PLACE_GENERAL_PHOTO_ROWS.includes(row)) {
      const file = row === 'logo' ? building.logo : building.image
      return (
        <FieldPhotoImage
          alt={t(getPlaceGeneralRowLabelKey(row))}
          src={getAssetLink({ file: file ?? undefined, path: 'chitown-building' })}
        />
      )
    }
    const value = values[row as keyof typeof values] as string
    if (value && row === 'email') return <FieldExternalLink kind="email" value={value} />
    if (value && PLACE_GENERAL_PHONE_ROWS.includes(row)) {
      return <FieldExternalLink kind="phone" value={value} />
    }
    if (value && PLACE_GENERAL_LINK_ROWS.includes(row))
      return <FieldExternalLink kind="url" value={value} />
    return value
  }

  return (
    <FieldsTable>
      {getPlaceGeneralLayout(isAdmin).map(({ group, rows }) => (
        <FieldGroup key={group}>
          <FieldGroupHeader>{t(`organisation:places.general.groups.${group}`)}</FieldGroupHeader>
          {rows.map((row) =>
            PLACE_GENERAL_FLAG_ROWS.includes(row) ? (
              <PlaceFeatureRow
                key={row}
                building={building}
                featureKey={row as PlaceGeneralFlagKey}
                isAdmin={isAdmin}
                isEditing={false}
                layout="field"
              />
            ) : (
              <FieldRow
                key={row}
                hint={
                  row === 'residentConvenienceFeeRate'
                    ? t('organisation:places.building.fields.residentConvenienceFeeRate.hint')
                    : undefined
                }
                photo={PLACE_GENERAL_PHOTO_ROWS.includes(row)}
                label={t(getPlaceGeneralRowLabelKey(row))}
              >
                <FieldViewValue>{renderValue(row)}</FieldViewValue>
              </FieldRow>
            ),
          )}
        </FieldGroup>
      ))}
    </FieldsTable>
  )
}
